// src/pages/POS.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  query,
  where,
  orderBy,
  startAt,
  endAt,
} from "firebase/firestore";
import { db, auth } from "../services/firebase";
import BarcodeInput from "../components/BarcodeInput";
import CartPanel from "../components/CartPanel";
import PaymentDialog from "../components/PaymentDialog";
import { commitSaleAndStock } from "../services/posApi";
import { onAuthStateChanged } from "firebase/auth";
// ⬇️ NEW
import { recordInventoryMove } from "../services/stockMoves";

// ==== Config ====
const DEFAULT_LOCATION_ID = "boutique"; // ajuste si besoin
const SUGGEST_LIMIT = 24;

// ==== Hook debounce léger ====
function useDebounced(value, delay = 220) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function POS() {
  // Panier
  const [lines, setLines] = useState([]); // [{ itemId, name, price, qty, taxRate, cost }]
  const [message, setMessage] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const [activeId, setActiveId] = useState(null);

  // Caissier
  const [cashier, setCashier] = useState(null);
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setCashier(u));
    return () => unsub();
  }, []);

  // Recherche
  const [term, setTerm] = useState("");
  const debounced = useDebounced(term);
  const [nameResults, setNameResults] = useState([]); // {id, name, price, taxRate, cost, barcode?}
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const resultsBoxRef = useRef(null);
  const [payResetKey, setPayResetKey] = useState(0);

  // Totaux
  const totals = useMemo(() => {
    const sub = lines.reduce((s, l) => s + Number(l.price || 0) * Number(l.qty || 0), 0);
    const tax = lines.reduce(
      (s, l) => s + (l.taxRate ? l.price * l.qty * (Number(l.taxRate) / 100) : 0),
      0
    );
    const disc = 0;
    return {
      subTotal: sub,
      taxTotal: tax,
      discountTotal: disc,
      grandTotal: sub + tax - disc,
    };
  }, [lines]);

  // ====== Utilitaires ======
  function addLineFromItemDoc(id, it) {
    const line = {
      itemId: id,
      name: it.name || it.title || "Article",
      price: Number(it.price || 0),
      qty: 1,
      taxRate: Number(it.taxRate || 0),
      cost: Number(it.cost || 0),
    };
    setLines((prev) => {
      const f = prev.find((x) => x.itemId === id);
      return f
        ? prev.map((x) => (x.itemId === id ? { ...x, qty: x.qty + 1 } : x))
        : [...prev, line];
    });
    setHighlightId(id);
    setActiveId(id);
    setTimeout(() => setHighlightId(null), 800);
  }

  // ====== Scan code-barres ======
  async function addByBarcode(code) {
    setMessage("");
    try {
      const qy = query(collection(db, "itemsEK"), where("barcode", "==", code), limit(1));
      const snap = await getDocs(qy);
      if (snap.empty) {
        setMessage("❌ Code inconnu : " + code);
        setActiveId(null);
        return;
      }
      const d = snap.docs[0];
      addLineFromItemDoc(d.id, d.data());
      setMessage(`✅ ${(d.data().name || "Article")} ajouté`);
      setNameResults([]);
      setActiveIdx(-1);
    } catch (e) {
      console.error(e);
      setMessage("❌ Erreur lecture code-barres");
    }
  }

  // ====== Recherche nom / hybride ======
  useEffect(() => {
    let alive = true;
    (async () => {
      const t = debounced.trim();
      if (!t) {
        if (alive) setNameResults([]);
        return;
      }
      setLoading(true);
      try {
        const looksBarcode = /^\d{8,}$/.test(t);
        if (looksBarcode) {
          const bq = query(collection(db, "itemsEK"), where("barcode", "==", t), limit(1));
          const bsnap = await getDocs(bq);
          if (!alive) return;
          if (!bsnap.empty) {
            const bd = bsnap.docs[0];
            setNameResults([{ id: bd.id, ...bd.data() }]);
            setActiveIdx(0);
            return;
          }
        }

        const lower = t.toLowerCase();
        try {
          const nq = query(
            collection(db, "itemsEK"),
            orderBy("nameLower"),
            startAt(lower),
            endAt(lower + "\uf8ff"),
            limit(SUGGEST_LIMIT)
          );
          const nsnap = await getDocs(nq);
          if (!alive) return;
          const arr = nsnap.docs.map((d) => ({ id: d.id, ...d.data() }));
          if (arr.length) {
            setNameResults(arr);
            setActiveIdx(0);
            return;
          }
        } catch {
          // pas d'index nameLower => fallback
        }

        const fsnap = await getDocs(query(collection(db, "itemsEK"), limit(100)));
        if (!alive) return;
        const arr = fsnap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((it) => (it.name || it.title || "").toLowerCase().includes(lower))
          .slice(0, SUGGEST_LIMIT);
        setNameResults(arr);
        setActiveIdx(arr.length ? 0 : -1);
      } catch (e) {
        console.error(e);
        if (alive) setNameResults([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [debounced]);

  function pickResult(it) {
    addLineFromItemDoc(it.id, it);
    setTerm("");
    setNameResults([]);
    setActiveIdx(-1);
    resultsBoxRef.current?.blur?.();
  }

  function onSearchKeyDown(e) {
    if (!nameResults.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((p) => (p + 1) % nameResults.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((p) => (p - 1 + nameResults.length) % nameResults.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = nameResults[Math.max(0, activeIdx)];
      if (it) pickResult(it);
    } else if (e.key === "Escape") {
      setNameResults([]);
      setActiveIdx(-1);
    }
  }

  // ====== Panier ======
  function inc(id) {
    setLines((p) => p.map((l) => (l.itemId === id ? { ...l, qty: l.qty + 1 } : l)));
    setActiveId(id);
  }
  function dec(id) {
    setLines((p) =>
      p.map((l) => (l.itemId === id ? { ...l, qty: Math.max(1, l.qty - 1) } : l))
    );
    setActiveId(id);
  }
  function removeLine(id) {
    setLines((p) => p.filter((l) => l.itemId !== id));
    setActiveId(null);
  }

  // ====== Paiement ======
  async function onConfirmPayment(payments) {
    if (!lines.length) return;

    const norm = (x) => Number(x || 0);

    const sale = {
      items: lines.map((l) => ({
        ...l,
        lineTotal: norm(l.price) * norm(l.qty),
      })),
      subTotal: norm(totals.subTotal),
      taxTotal: norm(totals.taxTotal),
      discountTotal: norm(totals.discountTotal),
      grandTotal: norm(totals.grandTotal),
      payments: (payments || []).map((p) => ({
        method: (p.method || "").toLowerCase(),
        amount: norm(p.amount),
        ref: p.ref || null,
      })),
    };

    // ---- Normalisation des paiements (cash / change) ----
    const nonCashPaid = sale.payments
      .filter((p) => p.method !== "cash")
      .reduce((s, p) => s + p.amount, 0);

    const totalDueFromCash = Math.max(0, sale.grandTotal - nonCashPaid);

    const cashGiven = sale.payments
      .filter((p) => p.method === "cash")
      .reduce((s, p) => s + p.amount, 0);

    const effectiveCashIn = Math.min(cashGiven, totalDueFromCash);
    const change = Math.max(0, cashGiven - effectiveCashIn);

    const normalizedPayments = [
      ...sale.payments.filter((p) => p.method !== "cash"),
      ...(effectiveCashIn > 0 ? [{ method: "cash", amount: effectiveCashIn, ref: null }] : []),
      ...(change > 0 ? [{ method: "cash_change", amount: -change, ref: "change" }] : []),
    ];

    // ---- Session de caisse ----
    let sessionId = null;
    const lsId = localStorage.getItem("activeCashSessionId");
    const lsUid = localStorage.getItem("activeCashSessionUid");

    if (lsId && lsUid && cashier?.uid && lsUid === cashier.uid) {
      sessionId = lsId;
    } else {
      sessionId = null;
    }

    if (effectiveCashIn > 0 && !sessionId) {
      setMessage(
        "⚠️ Aucune session de caisse ouverte : l’encaissement espèces ne sera pas enregistré dans la caisse."
      );
    } else {
      setMessage("");
    }

    try {
      const cashierId = cashier?.uid || "anonymous";

      // 1) Vente + stock
      const { saleId } = await commitSaleAndStock({
        sale: { ...sale, payments: normalizedPayments },
        locationId: DEFAULT_LOCATION_ID,
        cashierId,
        sessionId,
      });

      // 2) Mouvements de stock (une ligne par article)
      await Promise.all(
        lines.map((l) =>
          recordInventoryMove({
            itemId: l.itemId,
            locationId: DEFAULT_LOCATION_ID,
            type: "sale",
            delta: -Number(l.qty || 0),      // sort du stock
            note: `Vente #${saleId}`,
            ref: saleId,
          })
        )
      );

      setMessage(`✅ Vente enregistrée #${saleId}`);
      setLines([]);
      setActiveId(null);
      setPayResetKey((k) => k + 1);
    } catch (e) {
      console.error(e);
      setMessage("❌ Erreur : " + e.message);
    }
  }

  // ====== UI ======
  return (
    <div className="container-xxl">
      {/* Barre d'outils / contexte */}
      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        <div className="d-flex flex-column">
          <h4 className="m-0">Point de Vente</h4>
          <small className="text-muted">
            Lieu : <code>{DEFAULT_LOCATION_ID}</code>
            {" · "}
            Caissier : <code>{cashier?.email || "anonyme"}</code>
          </small>
        </div>
        <div className="d-flex align-items-center gap-3">
          <div className="text-end">
            <div className="small text-muted">Total à payer</div>
            <div className="h5 m-0">{totals.grandTotal.toLocaleString()} FCFA</div>
          </div>
          <PaymentDialog
            key={payResetKey}
            total={totals.grandTotal}
            onConfirm={onConfirmPayment}
          />
        </div>
      </div>

      <div className="row g-3">
        {/* Colonne gauche : Scan / Recherche */}
        <div className="col-12 col-lg-5">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <h6 className="card-title mb-3">Scanner / Rechercher un article</h6>

              <BarcodeInput onDetect={addByBarcode} />

              <div className="mt-3 position-relative">
                <input
                  ref={resultsBoxRef}
                  className="form-control"
                  type="search"
                  placeholder="Nom produit ou code-barres…"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  autoComplete="off"
                />
                {(term || loading) && (
                  <div
                    className="list-group position-absolute w-100 z-3 mt-1 shadow-sm"
                    style={{ maxHeight: 320, overflowY: "auto" }}
                  >
                    {loading && (
                      <div className="list-group-item small text-muted">Recherche…</div>
                    )}
                    {!loading &&
                      nameResults.map((it, idx) => (
                        <button
                          key={it.id}
                          type="button"
                          className={
                            "list-group-item list-group-item-action d-flex justify-content-between align-items-center" +
                            (idx === activeIdx ? " active" : "")
                          }
                          onClick={() => pickResult(it)}
                        >
                          <div className="text-start">
                            <div className="fw-semibold">{it.name || it.title || "Article"}</div>
                            <small className="text-muted">
                              {it.barcode ? `CB: ${it.barcode}` : "—"} · Prix :{" "}
                              {Number(it.price || 0).toLocaleString()} FCFA
                            </small>
                          </div>
                          <span className="badge bg-light text-dark">+ Ajouter</span>
                        </button>
                      ))}
                    {!loading && !nameResults.length && term && (
                      <div className="list-group-item small text-muted">
                        Aucun résultat pour “{term}”
                      </div>
                    )}
                  </div>
                )}
              </div>

              {message && (
                <div
                  className={`alert mt-3 py-2 ${
                    message.startsWith("✅")
                      ? "alert-success"
                      : message.startsWith("❌")
                      ? "alert-danger"
                      : "alert-info"
                  }`}
                >
                  {message}
                </div>
              )}

              <div className="mt-3 small text-muted">
                Astuce : tape 2 lettres minimum pour chercher, ↵ pour ajouter le 1er résultat.
                Scanne un code-barres pour ajouter directement.
              </div>
            </div>
          </div>
        </div>

        {/* Colonne droite : Panier + Paiement */}
        <div className="col-12 col-lg-7">
          <div className="position-sticky" style={{ top: 12 }}>
            <CartPanel
              lines={lines}
              onInc={inc}
              onDec={dec}
              onRemove={removeLine}
              totals={totals}
              highlightId={highlightId}
              activeId={activeId}
            />

            <div className="d-lg-none mt-3">
              <PaymentDialog
                key={`m-${payResetKey}`}
                total={totals.grandTotal}
                onConfirm={onConfirmPayment}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
